/**
 * Runtime privacy guard.
 *
 * Wraps the network primitives the browser exposes (`fetch`,
 * `XMLHttpRequest`, `WebSocket`, `EventSource`, `navigator.sendBeacon`) and
 * keeps a tally of every outbound call attempted by the running app.
 *
 * This is observation-only by default - calls are NOT blocked, because some
 * benign asset loads (sourcemaps in dev, the Lovable preview shell, etc.)
 * legitimately go over the network. The Privacy page reads the tally and
 * shows it as a live signal: in normal navigation it should stay at 0.
 *
 * Excluded by design:
 *   - <img>, <script>, <link>, <iframe>, navigation requests - these are
 *     resource loads from the same origin (the SPA bundle) and don't carry
 *     user input.
 *   - Lovable's preview/devtools messaging (postMessage), which doesn't
 *     touch any of the wrapped APIs.
 */

export type NetworkCall = {
  api: "fetch" | "xhr" | "websocket" | "eventsource" | "sendBeacon";
  url: string;
  at: number;
  /**
   * Whether the call originates from the hosting platform (e.g. Lovable
   * preview/published-app instrumentation) rather than ElementPad's own
   * code. We surface these separately so a host-injected ping doesn't look
   * like a privacy regression.
   */
  source: "app" | "host";
  /**
   * The pathname of the current route at the moment the call was recorded.
   * Lets the privacy checklist attribute traffic to Play, Learn, Settings,
   * etc. Empty string in non-browser contexts.
   */
  route: string;
};

type Listener = (calls: ReadonlyArray<NetworkCall>) => void;

const calls: NetworkCall[] = [];
const listeners = new Set<Listener>();
let installed = false;

/**
 * Patterns for URLs we know come from the hosting platform, not from
 * ElementPad's source code. These never carry user input from our app.
 * Keep the list narrow - anything not matched is treated as an app call
 * and flagged loudly.
 */
const HOST_URL_PATTERNS: RegExp[] = [
  /\/~api\//, // Lovable preview/runtime control + analytics endpoints
  /lovable(project|\.dev|\.app)/i, // Lovable hosting domains
  /gpteng\.co/i, // Lovable script CDN
];

function classifySource(url: string): NetworkCall["source"] {
  return HOST_URL_PATTERNS.some((re) => re.test(url)) ? "host" : "app";
}

function record(api: NetworkCall["api"], url: string) {
  const safeUrl = String(url).slice(0, 256);
  const route =
    typeof window !== "undefined" && window.location
      ? window.location.pathname
      : "";
  const entry: NetworkCall = {
    api,
    url: safeUrl,
    at: Date.now(),
    source: classifySource(safeUrl),
    route,
  };
  calls.push(entry);
  // Cap memory: only retain the last 50 entries.
  if (calls.length > 50) calls.splice(0, calls.length - 50);
  for (const fn of listeners) {
    try {
      fn(calls);
    } catch {
      /* ignore listener errors */
    }
  }
  if (typeof console !== "undefined") {
    // Loud-ish warning so anyone with DevTools open notices, but quieter
    // for known-safe host calls.
    const msg = `[privacy-guard] ${entry.source} ${api} -> ${entry.url}`;
    // eslint-disable-next-line no-console
    if (entry.source === "host") console.info(msg);
    // eslint-disable-next-line no-console
    else console.warn(msg);
  }
}

/**
 * Install the guard exactly once. Safe to call repeatedly.
 * Should be invoked from the app entry (`src/main.tsx`) before any code
 * that might issue requests.
 */
export function installNetworkGuard(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // fetch
  if (typeof window.fetch === "function") {
    const orig = window.fetch.bind(window);
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : (input as Request).url;
      record("fetch", url);
      return orig(input as RequestInfo, init);
    };
  }

  // XMLHttpRequest
  if (typeof window.XMLHttpRequest === "function") {
    const OrigOpen = window.XMLHttpRequest.prototype.open;
    window.XMLHttpRequest.prototype.open = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      record("xhr", typeof url === "string" ? url : url.href);
      return (OrigOpen as (...a: unknown[]) => void).call(this, method, url, ...rest);
    } as typeof OrigOpen;
  }

  // WebSocket
  if (typeof window.WebSocket === "function") {
    const OrigWS = window.WebSocket;
    const Wrapped = function (
      this: WebSocket,
      url: string | URL,
      protocols?: string | string[],
    ) {
      record("websocket", typeof url === "string" ? url : url.href);
      return new OrigWS(url, protocols);
    } as unknown as typeof WebSocket;
    Wrapped.prototype = OrigWS.prototype;
    Object.assign(Wrapped, OrigWS);
    window.WebSocket = Wrapped;
  }

  // EventSource (SSE)
  if (typeof window.EventSource === "function") {
    const OrigES = window.EventSource;
    const Wrapped = function (
      this: EventSource,
      url: string | URL,
      init?: EventSourceInit,
    ) {
      record("eventsource", typeof url === "string" ? url : url.href);
      return new OrigES(url, init);
    } as unknown as typeof EventSource;
    Wrapped.prototype = OrigES.prototype;
    Object.assign(Wrapped, OrigES);
    window.EventSource = Wrapped;
  }

  // navigator.sendBeacon
  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    const orig = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = (url: string | URL, data?: BodyInit | null) => {
      record("sendBeacon", typeof url === "string" ? url : url.href);
      return orig(url, data);
    };
  }
}

export function getNetworkCalls(): ReadonlyArray<NetworkCall> {
  return calls;
}

export function subscribeNetworkCalls(fn: Listener): () => void {
  listeners.add(fn);
  // Fire once with current state so subscribers don't miss prior calls.
  fn(calls);
  return () => {
    listeners.delete(fn);
  };
}